import { Task, TaskState, TaskStatus, MongoTask } from './types'
import { DropResult } from '@hello-pangea/dnd'
import { UseToastOptions } from '@chakra-ui/react'
import React, { useState, useCallback, useEffect } from 'react'
import { api } from './api'

// Initialize empty task state
export const initialTasks: TaskState = {
  tasks: {},
  columns: {
    'todo': {
      id: 'todo',
      title: 'To Do',
      taskIds: []
    },
    'inProgress': {
      id: 'inProgress',
      title: 'In Progress',
      taskIds: []
    },
    'completed': {
      id: 'completed',
      title: 'Completed',
      taskIds: []
    }
  },
  columnOrder: ['todo', 'inProgress', 'completed']
}

// Handles drag end logic
export const handleDragEnd = async (result: DropResult, taskState: TaskState, setTaskState: React.Dispatch<React.SetStateAction<TaskState>>) => {
  const { destination, source, draggableId } = result

  // If there's no destination or if the item is dropped in the same place
  if (!destination ||
      (destination.droppableId === source.droppableId &&
       destination.index === source.index)) {
    return
  }

  // Get source and destination columns
  const sourceColumn = taskState.columns[source.droppableId]
  const destinationColumn = taskState.columns[destination.droppableId]

  // Handle moving within the same column
  if (sourceColumn === destinationColumn) {
    const newTaskIds = Array.from(sourceColumn.taskIds)
    // Remove from old position and insert at new position
    newTaskIds.splice(source.index, 1)
    newTaskIds.splice(destination.index, 0, draggableId)

    // Create updated column
    const newColumn = {
      ...sourceColumn,
      taskIds: newTaskIds
    }

    // Update state
    setTaskState(prev => ({
      ...prev,
      columns: {
        ...prev.columns,
        [newColumn.id]: newColumn
      }
    }))
    return
  }

  // For moving between columns, update UI first
  const sourceTaskIds = Array.from(sourceColumn.taskIds)
  sourceTaskIds.splice(source.index, 1)

  const destinationTaskIds = Array.from(destinationColumn.taskIds)
  destinationTaskIds.splice(destination.index, 0, draggableId)

  const updatedTask = {
    ...taskState.tasks[draggableId],
    status: destination.droppableId as TaskStatus
  }

  // Update UI immediately
  setTaskState(prev => ({
    ...prev,
    tasks: {
      ...prev.tasks,
      [draggableId]: updatedTask
    },
    columns: {
      ...prev.columns,
      [sourceColumn.id]: {
        ...sourceColumn,
        taskIds: sourceTaskIds
      },
      [destinationColumn.id]: {
        ...destinationColumn,
        taskIds: destinationTaskIds
      }
    }
  }))

  // Then update backend
  try {
    await api.updateTask({
      _id: updatedTask.id,
      title: updatedTask.text,
      description: updatedTask.description,
      status: updatedTask.status
    })

    // Update state with new columns and updated task
    setTaskState(prev => ({
      ...prev,
      tasks: {
        ...prev.tasks,
        [draggableId]: updatedTask
      },
      columns: {
        ...prev.columns,
        [sourceColumn.id]: {
          ...sourceColumn,
          taskIds: sourceTaskIds
        },
        [destinationColumn.id]: {
          ...destinationColumn,
          taskIds: destinationTaskIds
        }
      }
    }))
  } catch (error) {
    console.error('Error updating task status:', error)
  }
}

// Create a task
export const createTask = (
  newTask: { text: string, description: string },
  taskState: TaskState,
  setTaskState: React.Dispatch<React.SetStateAction<TaskState>>
) => {
  // Generate a new task ID
  const newTaskId = `task-${Date.now()}`

  // Create new task object
  const task: Task = {
    id: newTaskId,
    text: newTask.text,
    description: newTask.description,
    status: 'todo'
  }

  // Update state with new task
  setTaskState(prev => {
    const newTodoColumn = {
      ...prev.columns['todo'],
      taskIds: [...prev.columns['todo'].taskIds, newTaskId]
    }

    return {
      ...prev,
      tasks: {
        ...prev.tasks,
        [newTaskId]: task
      },
      columns: {
        ...prev.columns,
        'todo': newTodoColumn
      }
    }
  })

  return task
}

// Update a task
export const updateTask = (
  editingTask: Task,
  taskState: TaskState,
  setTaskState: React.Dispatch<React.SetStateAction<TaskState>>
) => {
  setTaskState(prev => ({
    ...prev,
    tasks: {
      ...prev.tasks,
      [editingTask.id]: editingTask
    }
  }))
}

// Delete a task
// Custom hook for task management
export const useTaskManagement = (toast: (options: UseToastOptions) => void) => {
  const [taskState, setTaskState] = useState<TaskState>(initialTasks)
  const [newTask, setNewTask] = useState({ text: '', description: '' })
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [isEditing, setIsEditing] = useState(false)

  // Fetch tasks on component mount
  const fetchTasks = useCallback(async () => {
    try {
      const tasks = await api.getTasks()
      
      // Create a fresh task state with empty columns
      const newTaskState: TaskState = {
        ...initialTasks,
        tasks: {} as { [key: string]: Task },
        columns: {
          'todo': { ...initialTasks.columns['todo'], taskIds: [] },
          'inProgress': { ...initialTasks.columns['inProgress'], taskIds: [] },
          'completed': { ...initialTasks.columns['completed'], taskIds: [] }
        }
      }

      // Organize tasks into columns
      tasks.forEach((mongoTask: MongoTask) => {
        const task: Task = {
          id: mongoTask._id,
          text: mongoTask.title,
          description: mongoTask.description,
          status: mongoTask.status
        }
        newTaskState.tasks[task.id] = task
        const column = newTaskState.columns[task.status]
        if (column) {
          column.taskIds.push(task.id)
        }
      })

      setTaskState(newTaskState)
    } catch (error) {
      console.error('Error fetching tasks:', error)
      toast({
        title: 'Error fetching tasks',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }, [toast, setTaskState])

  // Fetch tasks on component mount
  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  const deleteTask = useCallback(async (taskId: string) => {
    const taskStatus = taskState.tasks[taskId]?.status
    if (!taskStatus) return

    try {
      // Delete from backend
      await api.deleteTask(taskId)

      // Create a copy of the tasks and remove the deleted task
      const newTasks = { ...taskState.tasks }
      delete newTasks[taskId]

      // Remove task ID from its column
      const newTaskIds = taskState.columns[taskStatus].taskIds.filter(id => id !== taskId)
      const updatedColumn = {
        ...taskState.columns[taskStatus],
        taskIds: newTaskIds
      }

      // Update state
      setTaskState(prev => ({
        ...prev,
        tasks: newTasks,
        columns: {
          ...prev.columns,
          [taskStatus]: updatedColumn
        }
      }))

      toast({
        title: 'Task deleted',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error) {
      console.error('Error deleting task:', error)
      toast({
        title: 'Error deleting task',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }, [taskState, toast])

  const handleAddTask = useCallback(async (onClose: () => void) => {
    if (!newTask.text.trim()) {
      toast({
        title: 'Task title required',
        status: 'error',
        duration: 2000,
        isClosable: true
      })
      return
    }

    try {
      // Create task in backend
      const createdTask = await api.createTask({
        title: newTask.text,
        description: newTask.description,
        status: 'todo'
      })

      // Convert MongoDB task to frontend task
      const frontendTask: Task = {
        id: createdTask._id,
        text: createdTask.title,
        description: createdTask.description,
        status: createdTask.status
      }

      // Update state with new task
      setTaskState(prev => {
        const newTodoColumn = {
          ...prev.columns['todo'],
          taskIds: [...prev.columns['todo'].taskIds, frontendTask.id]
        }

        return {
          ...prev,
          tasks: {
            ...prev.tasks,
            [frontendTask.id]: frontendTask
          },
          columns: {
            ...prev.columns,
            'todo': newTodoColumn
          }
        }
      })
      
      // Reset form and close modal
      setNewTask({ text: '', description: '' })
      onClose()

      toast({
        title: 'Task created',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error) {
      console.error('Error creating task:', error)
      toast({
        title: 'Error creating task',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }, [newTask, toast])

  const handleEditTask = useCallback(async (onClose: () => void) => {
    if (!editingTask || !editingTask.text.trim()) {
      toast({
        title: 'Task title required',
        status: 'error',
        duration: 2000,
        isClosable: true
      })
      return
    }

    try {
      // Update task in backend
      const updatedTask = await api.updateTask({
        _id: editingTask.id,
        title: editingTask.text,
        description: editingTask.description,
        status: editingTask.status
      })

      // Convert MongoDB task to frontend task
      const frontendTask: Task = {
        id: updatedTask._id,
        text: updatedTask.title,
        description: updatedTask.description,
        status: updatedTask.status
      }

      // Update state
      setTaskState(prev => ({
        ...prev,
        tasks: {
          ...prev.tasks,
          [editingTask.id]: frontendTask
        }
      }))
      
      // Reset and close modal
      setEditingTask(null)
      setIsEditing(false)
      onClose()

      toast({
        title: 'Task updated',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error) {
      console.error('Error updating task:', error)
      toast({
        title: 'Error updating task',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }, [editingTask, toast])

  const openEditModal = useCallback((task: Task, onOpen: () => void) => {
    setEditingTask({ ...task })
    setIsEditing(true)
    onOpen()
  }, [])

  const openAddModal = useCallback((onOpen: () => void) => {
    setNewTask({ text: '', description: '' })
    setIsEditing(false)
    onOpen()
  }, [])

  const handleDragEndWrapper = useCallback((result: DropResult) => 
    handleDragEnd(result, taskState, setTaskState), [taskState])

  return {
    taskState,
    newTask,
    setNewTask,
    editingTask,
    setEditingTask,
    isEditing,
    handleAddTask,
    handleEditTask,
    deleteTask,
    openEditModal,
    openAddModal,
    handleDragEnd: handleDragEndWrapper
  }
}